/**
 * Previous/next within one page of a filtered, server-ordered list.
 * The current page is the source of truth: an id that is not on it has left
 * the list (or was opened from somewhere else) and gets no neighbors.
 * Crossing a page boundary uses the adjacent page once that page has loaded.
 */

export type ListPage<T> = {
  items: readonly T[];
  /** False while the request has not succeeded. An error is loaded and empty. */
  loaded: boolean;
};

export type ResolvedListNeighbors<T> = {
  /** Index on the current page, or -1 when the id is not on it. */
  indexOnPage: number;
  /** 1-based position in the filtered list, or null when the id is absent. */
  position: number | null;
  needsPreviousPage: boolean;
  needsNextPage: boolean;
  previous: { item: T; page: number } | null;
  next: { item: T; page: number } | null;
};

export function resolveListNeighbors<T>(args: {
  currentId: string;
  idOf: (item: T) => string;
  /** 1-based page the list is showing. */
  page: number;
  pageSize: number;
  total: number;
  current: ListPage<T>;
  previousPage?: ListPage<T>;
  nextPage?: ListPage<T>;
}): ResolvedListNeighbors<T> {
  const indexOnPage = indexOfId(args);
  if (indexOnPage < 0) {
    return {
      indexOnPage,
      position: null,
      needsPreviousPage: false,
      needsNextPage: false,
      previous: null,
      next: null,
    };
  }

  const position = (args.page - 1) * args.pageSize + indexOnPage + 1;
  const needsPreviousPage = indexOnPage === 0 && args.page > 1;
  const needsNextPage =
    indexOnPage === args.current.items.length - 1 && position < args.total;

  return {
    indexOnPage,
    position,
    needsPreviousPage,
    needsNextPage,
    previous: previousNeighbor(args, indexOnPage, needsPreviousPage),
    next: nextNeighbor(args, indexOnPage, needsNextPage),
  };
}

function indexOfId<T>(args: {
  currentId: string;
  idOf: (item: T) => string;
  current: ListPage<T>;
}): number {
  if (!args.current.loaded) return -1;
  return args.current.items.findIndex(
    (item) => args.idOf(item) === args.currentId,
  );
}

function previousNeighbor<T>(
  args: {
    page: number;
    current: ListPage<T>;
    previousPage?: ListPage<T>;
  },
  indexOnPage: number,
  needsPreviousPage: boolean,
): { item: T; page: number } | null {
  if (!needsPreviousPage) {
    const item = args.current.items[indexOnPage - 1];
    return item === undefined ? null : { item, page: args.page };
  }
  const previousPage = args.previousPage;
  if (!previousPage?.loaded || previousPage.items.length === 0) return null;
  const item = previousPage.items[previousPage.items.length - 1];
  return item === undefined ? null : { item, page: args.page - 1 };
}

function nextNeighbor<T>(
  args: {
    page: number;
    current: ListPage<T>;
    nextPage?: ListPage<T>;
  },
  indexOnPage: number,
  needsNextPage: boolean,
): { item: T; page: number } | null {
  if (!needsNextPage) {
    const item = args.current.items[indexOnPage + 1];
    return item === undefined ? null : { item, page: args.page };
  }
  const nextPage = args.nextPage;
  if (!nextPage?.loaded || nextPage.items.length === 0) return null;
  const item = nextPage.items[0];
  return item === undefined ? null : { item, page: args.page + 1 };
}

export type RecordListTarget = {
  id: string;
  /** 1-based page the target lives on. */
  page: number;
};

export type RecordListNavigationModel = {
  /** 1-based position in the filtered list. */
  position: number;
  total: number;
  /** An adjacent page is still loading, so one direction is not known yet. */
  pending: boolean;
  previous: RecordListTarget | null;
  next: RecordListTarget | null;
};

export type IdPage<T extends { id: string }> = {
  items: readonly T[];
  total: number;
  isSuccess: boolean;
  isError: boolean;
  /**
   * True when `items` still belong to the previous query key (`keepPreviousData`).
   * Those rows are not this page.
   */
  isPlaceholderData?: boolean;
};

/**
 * Turn the current page plus its neighbors into the controls' model.
 * Returns null when the open id is not on the current page.
 */
export function recordNavigationFromPages<T extends { id: string }>(args: {
  currentId: string;
  page: number;
  pageSize: number;
  current: IdPage<T>;
  previous: IdPage<T>;
  next: IdPage<T>;
}): RecordListNavigationModel | null {
  const resolved = resolveListNeighbors({
    currentId: args.currentId,
    idOf: (item) => item.id,
    page: args.page,
    pageSize: args.pageSize,
    total: args.current.total,
    current: { items: args.current.items, loaded: pageSettled(args.current) },
    previousPage: {
      items: args.previous.items,
      loaded: pageSettled(args.previous),
    },
    nextPage: { items: args.next.items, loaded: pageSettled(args.next) },
  });
  if (resolved.position === null) return null;
  return {
    position: resolved.position,
    total: args.current.total,
    pending: edgePending(resolved, args.previous, args.next),
    previous: toTarget(resolved.previous),
    next: toTarget(resolved.next),
  };
}

function edgePending<T extends { id: string }>(
  resolved: ResolvedListNeighbors<T>,
  previous: IdPage<T>,
  next: IdPage<T>,
): boolean {
  const previousPending =
    resolved.needsPreviousPage && !pageSettled(previous) && !previous.isError;
  const nextPending =
    resolved.needsNextPage && !pageSettled(next) && !next.isError;
  return previousPending || nextPending;
}

/** A page whose rows belong to this query key, not a previous one kept on screen. */
function pageSettled(page: {
  isSuccess: boolean;
  isPlaceholderData?: boolean;
}): boolean {
  return page.isSuccess && !page.isPlaceholderData;
}

/** Whether the previous and next page queries should run. */
export function adjacentPagesRequested(
  ready: boolean,
  edges: { needsPreviousPage: boolean; needsNextPage: boolean },
): { previous: boolean; next: boolean } {
  return {
    previous: ready && edges.needsPreviousPage,
    next: ready && edges.needsNextPage,
  };
}

function toTarget<T extends { id: string }>(
  neighbor: { item: T; page: number } | null,
): RecordListTarget | null {
  if (!neighbor) return null;
  return { id: neighbor.item.id, page: neighbor.page };
}
